import React, { useState } from 'react';
import { Mail, MapPin, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { contact } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { language } = useLanguage();
  const t = contact[language];
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      
      toast({
        title: language === 'en' ? 'Message Sent!' : 'تم إرسال الرسالة!',
        description: language === 'en' ? 'We will get back to you soon.' : 'سنرد عليك قريبًا.'
      });
      
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact error:', error);
      toast({
        title: language === 'en' ? 'Error' : 'خطأ',
        description: language === 'en' ? 'Failed to send message. Please try again.' : 'فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-page py-32">
      <div className="container mx-auto px-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-20 animate-fade-in">
          <h1 className="heading-1 text-gold mb-6">{t.title}</h1>
          <p className="body-large text-white/90">{t.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <Card className="bg-card-dark border-gold/20">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <Mail className="h-6 w-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="heading-6 text-gold mb-2">
                      {language === 'en' ? 'Email' : 'البريد الإلكتروني'}
                    </h3>
                    <a href={`mailto:${t.email}`} className="body-small text-gray-300 hover:text-gold transition-colors">
                      {t.email}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card-dark border-gold/20">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <MapPin className="h-6 w-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="heading-6 text-gold mb-2">
                      {language === 'en' ? 'Location' : 'الموقع'}
                    </h3>
                    <p className="body-small text-gray-300">{t.location}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card-dark border-gold/20">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <svg className="h-6 w-6 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="heading-6 text-gold mb-2">
                      {language === 'en' ? 'Website' : 'الموقع الإلكتروني'}
                    </h3>
                    <a href={`https://${t.website}`} target="_blank" rel="noopener noreferrer" className="body-small text-gray-300 hover:text-gold transition-colors">
                      {t.website}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-card-dark border-gold/20 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-white mb-2 block">{t.formLabels.name}</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="bg-white/5 border-white/20 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-white mb-2 block">{t.formLabels.email}</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="bg-white/5 border-white/20 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-white mb-2 block">{t.formLabels.subject}</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="bg-white/5 border-white/20 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white mb-2 block">{t.formLabels.message}</Label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      rows={6}
                      className="w-full rounded-md bg-white/5 border border-white/20 text-white p-3 resize-none focus:border-gold focus:ring-1 focus:ring-gold"
                    />
                  </div>

                  <Button type="submit" className="w-full btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {language === 'en' ? 'Sending...' : 'جاري الإرسال...'}
                      </>
                    ) : (
                      t.formLabels.submit
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;